import { ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { logService } from '@nsftx/seven-client-retail-utils'
import dmCoreSpaceOperatorApiService from '#src/services/dmCoreSpaceOperatorApiService'
import type { DMCoreSpaceOperatorLoginBodyData } from '#src/interfaces/DMCoreSpaceOperator'
import { useDmCoreSpaceOperatorStore } from '#src/stores/dmCoreSpaceOperatorStore'
import errorParser from '#src/utils/errorParser'
import DmCoreSpaceOperatorError from '#src/classes/DmCoreSpaceOperatorError'
import { getOperatorAccessTokenRefreshInterval } from '#src/utils/helper'

const LOG_PREFIX = '[dmCoreSpaceOperatorAuthStore]'

let refreshIntervalTimer: number | undefined

export const useDmCoreSpaceOperatorAuthStore = defineStore('dmCoreSpaceOperatorAuthStore', () => {
  const isLoginLoading = ref(false)
  const isLogoutLoading = ref(false)

  const loginOperator = async (loginData: DMCoreSpaceOperatorLoginBodyData) => {
    isLoginLoading.value = true

    try {
      const { setOperatorLoginData } = useDmCoreSpaceOperatorStore()

      const data = await dmCoreSpaceOperatorApiService.loginOperator(loginData)
      setOperatorLoginData(data)

      return data
    } catch (error) {
      logService.error(`${LOG_PREFIX} Error occurred during operator login.`, {
        code: 'DM_CORE_SPACE_OPERATOR_LOGIN_ERROR',
        ...errorParser.parseUpstream(error)
      })

      throw error
    } finally {
      isLoginLoading.value = false
    }
  }

  const getOperatorData = async () => {
    const { operatorLoginData, setOperatorData } = useDmCoreSpaceOperatorStore()

    try {
      const data = await dmCoreSpaceOperatorApiService.getOperatorData()
      setOperatorData(data)
    } catch (error) {
      const { upstream_message } = errorParser.parseUpstream(error)
      const code = 'DM_CORE_SPACE_GET_OPERATOR_DATA_ERROR'

      logService.error(`${LOG_PREFIX} Error occurred while getting operator data.`, {
        code,
        details: {
          operatorUuid: operatorLoginData?.operator_uuid
        },
        ...errorParser.parseUpstream(error)
      })
      clearOperatorSession()

      throw new DmCoreSpaceOperatorError(upstream_message, code, { cause: error })
    }
  }

  const refreshOperatorAccessToken = async (refreshToken: string) => {
    const { setOperatorLoginData } = useDmCoreSpaceOperatorStore()

    try {
      const data = await dmCoreSpaceOperatorApiService.refreshOperatorAccessToken({
        refresh_token: refreshToken
      })

      setOperatorLoginData(data)
      logService.info(`${LOG_PREFIX} Operator token has been refreshed.`, {
        code: 'DM_CORE_SPACE_OPERATOR_REFRESH_TOKEN_SUCCESS'
      })
    } catch (error) {
      logService.error(`${LOG_PREFIX} Error occurred during operator token refresh.`, {
        code: 'DM_CORE_SPACE_OPERATOR_REFRESH_TOKEN_API_ERROR',
        ...errorParser.parseUpstream(error)
      })

      throw error
    }
  }

  const startOperatorAccessTokenAutoRefresh = (onError?: () => void) => {
    const { operatorLoginData } = storeToRefs(useDmCoreSpaceOperatorStore())

    if (refreshIntervalTimer) return

    if (!operatorLoginData.value?.access_token) {
      logService.info(
        `${LOG_PREFIX} Operator access token not found. Aborting token auto refresh.`,
        {
          code: 'DM_CORE_SPACE_OPERATOR_ACCESS_TOKEN_NOT_FOUND'
        }
      )

      return
    }

    const refreshIntervalInMs = getOperatorAccessTokenRefreshInterval(
      operatorLoginData.value.access_token
    )

    logService.debug(`${LOG_PREFIX} Starting operator access token auto refresh.`, {
      code: 'DM_CORE_SPACE_OPERATOR_START_TOKEN_AUTO_REFRESH',
      details: {
        refreshIntervalInMs
      }
    })

    refreshIntervalTimer = window.setInterval(async () => {
      logService.debug(`${LOG_PREFIX} Refreshing operator access token.`, {
        code: 'DM_CORE_SPACE_OPERATOR_REFRESH_TOKEN_ATTEMPT'
      })

      if (!operatorLoginData.value?.refresh_token) {
        logService.info(
          `${LOG_PREFIX} Operator refresh token not found. Aborting token auto refresh.`,
          {
            code: 'DM_CORE_SPACE_OPERATOR_REFRESH_TOKEN_NOT_FOUND'
          }
        )
        stopOperatorAccessTokenAutoRefresh()

        return
      }

      try {
        await refreshOperatorAccessToken(operatorLoginData.value.refresh_token)
      } catch (error) {
        logService.error(`${LOG_PREFIX} Operator token refresh failed.`, {
          code: 'DM_CORE_SPACE_OPERATOR_REFRESH_TOKEN_ERROR',
          ...errorParser.parseUpstream(error)
        })

        clearOperatorSession()
        onError?.()
      }
    }, refreshIntervalInMs)
  }

  const stopOperatorAccessTokenAutoRefresh = () => {
    if (!refreshIntervalTimer) return

    window.clearInterval(refreshIntervalTimer)
    refreshIntervalTimer = undefined
  }

  const logoutOperator = async () => {
    isLogoutLoading.value = true

    try {
      await dmCoreSpaceOperatorApiService.logoutOperator()
      clearOperatorSession()
    } catch (error) {
      logService.error(`${LOG_PREFIX} Error occurred during operator logout.`, {
        code: 'DM_CORE_SPACE_OPERATOR_LOGOUT_API_ERROR',
        ...errorParser.parseUpstream(error)
      })

      throw error
    } finally {
      isLogoutLoading.value = false
    }
  }

  const clearOperatorSession = () => {
    const { removeOperatorLoginData, removeOperatorData } = useDmCoreSpaceOperatorStore()

    removeOperatorData()
    removeOperatorLoginData()
    stopOperatorAccessTokenAutoRefresh()
  }

  return {
    isLoginLoading,
    isLogoutLoading,
    loginOperator,
    getOperatorData,
    startOperatorAccessTokenAutoRefresh,
    stopOperatorAccessTokenAutoRefresh,
    refreshOperatorAccessToken,
    logoutOperator,
    clearOperatorSession
  }
})
